import React, { createContext, useState, useContext, useEffect } from 'react';
import logger from '../utils/logger';
import { analytics } from '../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { isCodexUi } from '../utils/codexEnv';

const THEME_KEY = 'nano-trading-theme';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      // First check localStorage
      const savedTheme = localStorage.getItem(THEME_KEY);
      if (savedTheme !== null) {
        return savedTheme === 'dark';
      }
      
      // If no saved preference, check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (error) {
      logger.warn('Error reading theme from localStorage:', error);
      return false; // Default to light theme if there's an error
    }
  });

  // Update localStorage and DOM when theme changes
  useEffect(() => {
    try {
      // Save to localStorage
      localStorage.setItem(THEME_KEY, isDarkMode ? 'dark' : 'light');
      
      // Update DOM
      document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
      
      // Log theme change event
      if (!isCodexUi && analytics) {
        logEvent(analytics, 'theme_changed', {
          theme: isDarkMode ? 'dark' : 'light',
          source: localStorage.getItem(THEME_KEY) ? 'user_preference' : 'system_preference'
        });
      }
      
      // Optional: Update meta theme-color for mobile browsers
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', isDarkMode ? '#1a1a1a' : '#ffffff');
      }
    } catch (error) {
      logger.warn('Error saving theme preference:', error);
    }
  }, [isDarkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (event) => {
      // Only update if there's no saved preference
      if (!localStorage.getItem(THEME_KEY)) {
        setIsDarkMode(event.matches);
      }
    };

    // Add listener for system theme changes
    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);

  const toggleTheme = () => {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode;
      try {
        localStorage.setItem(THEME_KEY, newMode ? 'dark' : 'light');
      } catch (error) {
        logger.warn('Error saving theme preference:', error);
      }
      return newMode;
    });
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 
